/**
 * PlattformNutzenSection — dritter Teil von Kapitel 4 «Unser Ansatz» (14.8.2026).
 *
 * Job: USP-Saeule 2 einloesen («Integration fast gratis», Fundament § 4). Die
 * Startseite fuehrte zwei der drei Saeulen aus — Saeule 1 in `howWeWork`,
 * Saeule 3 in `trust` — und die dritte gar nicht. Regel § 79 des Fundaments
 * («jede Aussenkommunikation fuehrt mit dem Dach oder genau einer Saeule»)
 * unterscheidet nicht zwischen den Saeulen.
 *
 * Ausloeser war Kundenfeedback nach Release: «unsere Technologien sind nicht als
 * USP und Enabler erkennbar».
 * Befund: docs/befunde/2026-08-14-technologie-usp-nach-release.md
 * Plan:   docs/plans/2026-08-14-technologie-als-usp-einloesen.md
 *
 * Der Ausgang auf /plattform ist nicht Beiwerk, sondern der Beleg in Sichtweite:
 * Dort ist Saeule 2 ausgefuehrt. Ohne ihn bliebe hier eine Behauptung stehen.
 *
 * Aufbau uebernommen von `HowWeWorkSection` — dieselbe Rolle im selben Kapitel,
 * nur ohne Bild-Variante. Vier Lehren von dort gelten weiter: Hooks vor jedem
 * bedingten Ausstieg, Textlink statt Knopf (der Ausgang fuehrt auf eine
 * Begruendungsseite, nicht in eine Handlung), `flex justify-center` weil
 * `SectionTextLink` `inline-flex` ist und `text-align` nicht folgt, und kein
 * festes Raster ohne Bild.
 */

import React from "react";
import { type SectionPlattformNutzenProps } from "../../content/section-plattform-nutzen";
import { SectionTextLink } from "../ui";
import { useLocalizedUrl } from "../../hooks";

export type PlattformNutzenSectionProps = SectionPlattformNutzenProps & {
    /**
     * Teil eines Kapitels statt eigenstaendige Sektion?
     *
     * Auf der Startseite immer `true`: Die Startseite steht auf ACHT Kapiteln
     * (GL-Beschluss § 5), eine neunte Sektion waere die siebte Ergaenzung einer
     * Reihe, deren Summe schon einmal zurueckgebaut werden musste. Die
     * Kapitelueberschrift steht als `h2`.
     *
     * **Seit dem 16.9.2026 laesst `nested` den Titel ganz weg** — dieselbe
     * Loesung wie in `ProblemSection`. Im Kapitel «Die Plattform» IST dieser
     * Titel die Kapitelueberschrift (`Homepage.tsx` gibt ihn an
     * `ChapterSection` weiter); die Sektion rendert ihn sonst ein zweites Mal,
     * direkt darunter, in kleinerer Schrift. Nachgemessen am Screenshot vom
     * 16.9.: «Bringen Sie Ihre Fachlichkeit. Alles andere steht schon.» stand
     * doppelt.
     */
    nested?: boolean;
};

export const PlattformNutzenSection: React.FC<PlattformNutzenSectionProps> = ({
    title,
    description,
    ctaText,
    ctaHref,
    nested = false,
}) => {
    // Vor dem bedingten Ausstieg — sonst laufen mit und ohne CTA verschiedene
    // Hooks (Rules of Hooks). Uebernommen aus `HowWeWorkSection`.
    const localizedCtaHref = useLocalizedUrl(ctaHref ?? "/plattform");

    return (
        <section data-testid="plattform-nutzen-section" className={nested ? "" : "bg-white py-24"}>
            <div className="mx-auto w-full px-4 md:px-12">
                <div className="mx-auto max-w-3xl text-center">
                    {!nested && <h2 className="mb-6 text-3xl font-black text-gray-900 md:text-4xl">{title}</h2>}
                    <p className="mb-8 text-lg leading-relaxed text-gray-600">{description}</p>
                    {ctaText && ctaHref && (
                        <div className="flex justify-center">
                            <SectionTextLink to={localizedCtaHref}>{ctaText}</SectionTextLink>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default PlattformNutzenSection;
